import React, { useState } from "react";
import toast from "react-hot-toast";
import { Button } from "../../../Components/ui/button";
import { Modal, ModalContent } from "../../../Components/ui/modal";
import Spinner from "../../../Assets/spinner";
import {
  useGetAllFeesQuery,
  useCreateFeeMutation,
  useUpdateFeeMutation,
  useDeleteFeeMutation,
} from "../AccountApi";
import FeeForm from "./FeeForm";

const DEFAULT_INITIAL_VALUES = {
  riderFlatFee: 0,
  riderPercentage: 75,
  serviceFees: [
    { orderType: "pickup", flatFee: 200, percentage: 0 },
    { orderType: "delivery", flatFee: 0, percentage: 5 },
    { orderType: "errand", flatFee: 500, percentage: 0 },
  ],
};

const toInitialValues = (fee) => {
  if (!fee) return DEFAULT_INITIAL_VALUES;
  return {
    riderFlatFee: fee.riderFee?.flatFee ?? 0,
    riderPercentage: fee.riderFee?.percentage ?? 0,
    serviceFees: (fee.serviceFee || []).map((sf) => ({
      orderType: sf.orderType || "",
      flatFee: sf.flatFee ?? 0,
      percentage: sf.percentage ?? 0,
    })),
  };
};

const FeeModal = () => {
  const [open, setOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);

  const { data, isLoading: loading } = useGetAllFeesQuery(
    { page: 1, limit: 20 },
    { skip: !open },
  );
  const [createFee, { isLoading: creating }] = useCreateFeeMutation();
  const [updateFee, { isLoading: updating }] = useUpdateFeeMutation();
  const [deleteFee, { isLoading: deleting }] = useDeleteFeeMutation();
  const saving = creating || updating;

  const existingFee = data?.data?.[0];

  const handleSubmit = async (values) => {
    const payload = {
      riderFee: {
        flatFee: values.riderFlatFee || 0,
        percentage: values.riderPercentage || 0,
      },
      serviceFee: values.serviceFees.map((sf) => ({
        orderType: sf.orderType,
        flatFee: sf.flatFee || 0,
        percentage: sf.percentage || 0,
      })),
      status: "active",
    };

    try {
      const res = existingFee
        ? await updateFee({ id: existingFee.id, body: payload }).unwrap()
        : await createFee(payload).unwrap();
      if (res?.statusCode < 300) {
        toast.success(existingFee ? "Fee updated" : "Fee created", {
          id: "feeSaved",
        });
        setOpen(false);
      }
    } catch (error) {
      toast.error("Could not save fee", { id: "feeSaved" });
    }
  };

  const handleConfirmDelete = async () => {
    try {
      const res = await deleteFee(existingFee.id).unwrap();
      if (res?.statusCode < 300) {
        toast.success("Fee deleted", { id: "feeDelete" });
        setDeleteOpen(false);
        setOpen(false);
      }
    } catch (error) {
      toast.error("Could not delete fee", { id: "feeDelete" });
    }
  };

  return (
    <>
      <Button onClick={() => setOpen(true)}>Manage Fees</Button>

      <Modal
        open={open}
        onOpenChange={(next) => {
          setOpen(next);
          if (!next) setDeleteOpen(false);
        }}
      >
        <ModalContent name={existingFee ? "Edit Fee" : "Create Fee"}>
          {loading ? (
            <div className='py-10 flex justify-center'>
              <Spinner />
            </div>
          ) : (
            <>
              <FeeForm
                key={existingFee?.id || "create"}
                mode={existingFee ? "edit" : "create"}
                initialValues={toInitialValues(existingFee)}
                submitting={saving}
                onSubmit={handleSubmit}
              />
              {existingFee && (
                <div className='flex justify-end mt-2'>
                  <Button
                    type='button'
                    variant='failed'
                    size='sm'
                    onClick={() => setDeleteOpen(true)}
                  >
                    Delete Fee
                  </Button>
                </div>
              )}
            </>
          )}
        </ModalContent>
      </Modal>

      <Modal
        open={deleteOpen}
        onOpenChange={(next) => {
          if (!next) setDeleteOpen(false);
        }}
      >
        <ModalContent name='Delete Fee?'>
          <p className='text-sm text-primaryBlack my-4'>
            Are you sure you want to delete this fee configuration? This cannot
            be undone.
          </p>
          <div className='flex justify-between items-center bg-white w-full'>
            <Button
              type='button'
              variant='outline'
              className='w-[45%]'
              onClick={() => setDeleteOpen(false)}
            >
              No
            </Button>
            <Button
              type='button'
              variant='failed'
              className='w-[45%]'
              disabled={deleting}
              onClick={handleConfirmDelete}
            >
              {deleting ? <Spinner /> : "Yes, Delete"}
            </Button>
          </div>
        </ModalContent>
      </Modal>
    </>
  );
};

export default FeeModal;
